import { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BreadcrumbHeading, Spinner, Typography } from '@weareopenr/component-library';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { useOptionGroup } from 'src/hooks/useOptionGroup';
import { Routes } from 'src/routes';
import styles from './OptionGroupDisplay.module.scss';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import PriceSchemeSelector from 'src/components/PriceSchemeSelector';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import ModifierCategory from 'src/components/ModifierCategory';
import { EditOptionGroupDetailsModal } from 'src/components/EditOptionGroupDetailsModal/EditOptionGroupDetailsModal';
import { EditOptionGroupPricingModal } from 'src/components/EditOptionGroupPricingModal';
import { EditOptionGroupExternalSystemsModal } from 'src/components/EditOptionGroupExternalSystemsModal/EditOptionGroupExternalSystemsModal';

export enum ActiveModal {
  DETAILS = 'details',
  PRICING = 'pricing',
  CHANNELS = 'channels',
  ASSIGN = 'assign',
}

export const OptionGroupDisplay = (): ReactElement => {
  const { selectedPriceSchemeId, setSelectedPriceSchemeId } = useSelectedPriceScheme();
  const { optionGroupId } = useParams<{ optionGroupId: string }>();
  const { data: optionGroup, isLoading } = useOptionGroup(optionGroupId, selectedPriceSchemeId);
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  const openModal = (modal: ActiveModal) => () => setActiveModal(modal);
  const closeModal = () => setActiveModal(null);

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  return (
    <>
      <EditOptionGroupDetailsModal
        isOpen={activeModal === ActiveModal.DETAILS}
        optionGroup={optionGroup}
        onClose={closeModal}
      />
      <EditOptionGroupPricingModal
        isOpen={activeModal === ActiveModal.PRICING}
        close={closeModal}
        optionGroup={optionGroup}
      />
      <EditOptionGroupExternalSystemsModal
        isOpen={activeModal === ActiveModal.CHANNELS}
        closeModal={closeModal}
        optionGroup={optionGroup}
      />
      <BreadcrumbHeading
        breadcrumbs={[
          { label: 'Product Library', path: Routes.PRODUCT_LIBRARY },
          { label: optionGroup.name, path: `${Routes.OPTION_GROUPS}/${optionGroupId}` },
        ]}
        subtitle={
          optionGroup.modification_date
            ? `Last updated at ${formatDate(new Date(optionGroup.modification_date))}`
            : undefined
        }
      />
      <div className={styles['container']}>
        <div className={styles['heading-container']}>
          <Typography size="xlarge" weight="semi-bold">
            Option Group Details
          </Typography>
          <PriceSchemeSelector value={selectedPriceSchemeId} onChange={setSelectedPriceSchemeId} />
        </div>
        <ModifierCategory
          optionGroup={optionGroup}
          index={0}
          openOptionGroupModal={openModal(ActiveModal.DETAILS)}
          openOptionGroupPricingModal={openModal(ActiveModal.PRICING)}
          openOptionGroupExternalSystemsModal={openModal(ActiveModal.CHANNELS)}
        />
      </div>
    </>
  );
};
